/**
 * BitcrusherModule — a bit-depth + sample-rate decimator via an AudioWorklet.
 *
 * The heavy lifting runs in `public/worklets/bitcrusher.worklet.js`. The worklet
 * processor must be loaded on the AudioContext via `engine.loadWorklet()` before
 * this module's `init()` is called, otherwise the AudioWorkletNode constructor
 * throws. main.ts handles that sequencing.
 *
 * Params:
 *   - Bits (1..16)       — target bit depth. 16 = transparent. 4 = crunchy.
 *                           1 = punishing square-wave-adjacent.
 *   - Freq (500..48000)  — target sample rate. Lowering it introduces aliasing
 *                           and the classic "old sampler" downsampled grit.
 *   - Mix  (0..1)         — dry/wet. Equal-power crossfade.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

const WORKLET_NAME = 'bitcrusher-processor';

export class BitcrusherModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'bitcrusher' as const;
  readonly displayName = 'CRUSH';
  readonly id: string;
  bypass = false;

  readonly bits = new NumericParameter({
    name: 'Bits',
    default: 12,
    min: 1,
    max: 16,
    curve: 'linear',
    smoothingMs: 0, // bits is effectively discrete — no point smoothing
    // Bias high — 1-bit on a random patch annihilates whatever was going on.
    randomSkew: 2,
    formatter: (v) => `${v.toFixed(1)}`,
  });
  readonly freq = new NumericParameter({
    name: 'Freq',
    default: 22050,
    min: 500,
    max: 48000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    // Bias high — low sample rates add crunch but at extremes silence things.
    randomSkew: 2,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v.toFixed(0)}`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 1,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    randomSkew: 0.7,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [this.bits, this.freq, this.mix];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('crush');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    try {
      this.worklet = new AudioWorkletNode(ctx, WORKLET_NAME);
    } catch (err) {
      // If the worklet isn't loaded yet, fall back to a pass-through so the
      // module still wires cleanly and the FX chain stays intact. Caller
      // should have awaited engine.loadWorklet() before calling init().
      console.warn(
        '[bitcrusher] worklet not available — running as pass-through.',
        err,
      );
      this.worklet = null;
    }

    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputMixNode);

    // Wet path (through worklet if available, else a straight gain for safety)
    if (this.worklet) {
      this.inputNode.connect(this.worklet);
      this.worklet.connect(this.wetGain);

      const bitsParam = this.worklet.parameters.get('bits');
      const freqParam = this.worklet.parameters.get('freq');
      if (bitsParam) this.bits.bindAudioParam(bitsParam, ctx);
      if (freqParam) this.freq.bindAudioParam(freqParam, ctx);
    } else {
      // Pass-through fallback.
      this.inputNode.connect(this.wetGain);
    }
    this.wetGain.connect(this.outputMixNode);

    this.applyMix(this.mix.value, true);
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  /** Mod-routing lookup — Bits / Freq are worklet AudioParams, Mix is not. */
  getAudioParam(paramName: string): AudioParam | null {
    if (!this.worklet) return null;
    if (paramName === 'Bits') return this.worklet.parameters.get('bits') ?? null;
    if (paramName === 'Freq') return this.worklet.parameters.get('freq') ?? null;
    return null;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.inputNode?.disconnect();
    this.worklet?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.outputMixNode?.disconnect();
    this.inputNode = null;
    this.outputMixNode = null;
    this.worklet = null;
    this.dryGain = null;
    this.wetGain = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyMix(mix: number, instant = false): void {
    if (!this.engine || !this.dryGain || !this.wetGain) return;
    const dry = Math.cos((mix * Math.PI) / 2);
    const wet = Math.sin((mix * Math.PI) / 2);
    const t = this.engine.context.currentTime;
    if (instant) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.02);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.02);
    }
  }
}
